import React, { useState, useEffect } from 'react';
import {
  Card,
  Grid,
  Container,
  CardHeader,
  CardContent,
  CardActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import DeleteIcon from '@material-ui/icons/Delete';

import { createMuiTheme } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';

import Header from '../../components/Header';

import { firestore } from '../../utils/firebase';
import { useAuth } from '../../utils/useAuth';
import { generateReport } from '../../utils/api';
import {
  calculateHours,
  formatDate,
  formatTime,
} from '../../components/TimeEntryTable/TimeEntryTable';

const theme = createMuiTheme({
  palette: {
    primary: {
      main: '#773CBD',
    },
    secondary: {
      main: '#773CBD',
    },
  },
});

const useStyles = makeStyles((theme) => ({
  mainContent: {
    marginTop: theme.spacing(20),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  marginOnMobile: {
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
  },
}));

const ManageEvents = () => {
  const [eventName, setEventName] = useState('');
  const [events, setEvents] = useState([]);

  const [userProfile, setUserProfile] = useState({
    isAdmin: false,
    firstName: '',
    lastName: '',
    email: '',
    initials: '—',
  });

  const { user } = useAuth();
  const classes = useStyles();

  useEffect(() => {
    if (user.uid) {
      firestore
        .collection('users')
        .doc(user.uid)
        .get()
        .then((doc) => {
          const { isAdmin, firstName, lastName, email } = doc.data();
          const initials = `${firstName[0]}${lastName[0]}`.toUpperCase();

          setUserProfile({ firstName, lastName, email, isAdmin, initials });
        })
        .catch((e) => console.error(e));
    }
  }, [user.uid]);

  useEffect(() => {
    const unsubscribe = firestore
      .collection('events')
      .onSnapshot((snapshot) => {
        const docs = snapshot.docs;
        const events = docs.map((d) => ({ id: d.id, ...d.data() }));

        setEvents(events);
      });

    return unsubscribe;
  }, []);

  const handleReportGeneration = (startDate, endDate, closeDialog) => {
    const allRows = [
      [
        'First Name',
        'Last Name',
        'Email',
        'Date',
        'Event',
        'Start',
        'End',
        'Hours',
      ],
    ];

    firestore
      .collectionGroup('log')
      .get()
      .then((logSnapshot) => {
        logSnapshot.forEach((logDoc) => {
          const {
            firstName,
            lastName,
            email,
            date,
            event,
            start,
            end,
            minutes,
          } = logDoc.data();

          const formattedDate = formatDate(date);
          const formattedStart = formatTime(start);
          const formattedEnd = formatTime(end);
          const hours = calculateHours(minutes);

          allRows.push([
            firstName,
            lastName,
            email,
            formattedDate,
            event,
            formattedStart,
            formattedEnd,
            hours,
          ]);
        });

        generateReport(
          allRows,
          { reportStart: startDate, reportEnd: endDate },
          closeDialog
        );
      });
  };

  const handleSubmitNewEvent = () => {
    firestore
      .collection('events')
      .add({ eventName })
      .then(() => setEventName(''))
      .catch((e) => alert('Could not add the event.'));
  };

  const handleDeleteEvent = (id) => {
    firestore.collection('events').doc(id).delete();
  };

  return (
    <ThemeProvider theme={theme}>
      <Header
        onReportGeneration={handleReportGeneration}
        initials={userProfile.initials}
        isAdmin={userProfile.isAdmin}
      />
      <Container maxWidth="lg" className={classes.mainContent}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} className={classes.marginOnMobile}>
            <Card>
              <CardHeader title="Add new event..." />
              <CardContent>
                <TextField
                  fullWidth
                  id="event"
                  label="Event Name"
                  type="text"
                  name="event"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                />
              </CardContent>
              <CardActions>
                <Button
                  className={classes.submitButton}
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleSubmitNewEvent}
                >
                  Submit
                </Button>
              </CardActions>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card>
              <CardHeader title="Manage existing events..." />
              <List>
                {events.map((e) => (
                  <ListItem key={e.id}>
                    <ListItemText primary={e.eventName} />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteEvent(e.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </ThemeProvider>
  );
};

export default ManageEvents;
